import React, { createContext, useContext, ReactNode } from "react"
import { useLocalStorageState } from "ahooks"
import { defaultColumnsVisibility } from "./TableView/DataTable/columnsConfig"

export type ColumnVisibility = Record<string, { isVisible: boolean }>

interface ColumnVisibilityContextType {
  columnVisibility: ColumnVisibility | undefined
  updateColumnVisibility: (columnId: string, isVisible: boolean) => void
  getVisibleColumns: () => string[]
}

const ColumnVisibilityContext = createContext<ColumnVisibilityContextType | undefined>(undefined)

export const ColumnVisibilityProvider = ({ children }: { children: ReactNode }) => {
  const [columnVisibility, setColumnVisibility] = useLocalStorageState<ColumnVisibility>(
    "guestsTableColumnVisibility",
    {
      defaultValue: defaultColumnsVisibility,
    },
  )

  const updateColumnVisibility = React.useCallback((columnId: string, isVisible: boolean) => {
    if (columnVisibility) {
      const updatedVisibility = {
        ...columnVisibility,
        [columnId]: { isVisible },
      }
      setColumnVisibility(updatedVisibility)
    }
  }, [columnVisibility, setColumnVisibility])

  const getVisibleColumns = React.useCallback(() => Object.entries(columnVisibility || {})
    .filter(([, config]) => config.isVisible)
    .map(([columnId]) => columnId), [columnVisibility])

  const value = React.useMemo(() => ({
    columnVisibility,
    updateColumnVisibility,
    getVisibleColumns,
  }), [columnVisibility, updateColumnVisibility, getVisibleColumns])

  return (
    <ColumnVisibilityContext.Provider value={value}>
      {children}
    </ColumnVisibilityContext.Provider>
  )
}

export const useColumnVisibilityContext = () => {
  const context = useContext(ColumnVisibilityContext)
  if (context === undefined) {
    throw new Error("useColumnVisibilityContext must be used within a ColumnVisibilityProvider")
  }

  return context
}
